import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft, Plus } from 'lucide-react';
import { SERVICES } from '../data/mock';
import Reveal from '../components/Reveal';

export default function Service() {
  const { slug } = useParams();
  const s = SERVICES[slug];
  if (!s) return <Navigate to="/" replace />;
  const accent = s.accent;

  return (
    <div data-testid={`service-page-${slug}`}>
      <section className="px-6 md:px-10 pt-32 md:pt-36 pb-16 md:pb-20">
        <div className="flex items-center justify-between mb-10 fade-up">
          <Link to="/" className="meta linkline inline-flex items-center gap-2"><ArrowLeft size={14} /> Home</Link>
          <span className="tag text-white" style={{ background: accent }}>{s.eyebrow}</span>
        </div>
        <h1 className="font-display h-display max-w-[14ch] fade-up">
          {s.title[0]} <span style={{ color: accent }}>{s.title[1]}</span>
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-8">
          <p className="md:col-span-6 md:col-start-7 body-lg fade-up fade-up-2">{s.intro}</p>
        </div>
      </section>

      <Reveal className="px-6 md:px-10 border-t border-[#e6e2dc]">
        <div className="grid grid-cols-2 md:grid-cols-4">
          {s.stats.map(([v, l], i) => (
            <div key={l} className={`py-10 md:py-14 ${i < 3 ? 'md:border-r border-[#e6e2dc] md:pr-8' : ''} ${i > 0 ? 'md:pl-8' : ''}`}>
              <p className="font-display text-4xl md:text-5xl leading-none" style={{ color: accent }}>{v}</p>
              <p className="meta mt-4">{l}</p>
            </div>
          ))}
        </div>
      </Reveal>

      <section className="px-6 md:px-10 py-20 md:py-28 bg-warm">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <Reveal className="md:col-span-6">
            <p className="eyebrow" style={{ color: accent }}>01 / The approach</p>
            <div className="aspect-[4/5] mt-8 overflow-hidden card-hover border border-[#e6e2dc]">
              <img src={s.image} alt={s.alt} className="w-full h-full object-cover card-media" />
            </div>
          </Reveal>
          <div className="md:col-span-6">
            {s.points.map((p, i) => (
              <Reveal key={p.n} delay={i * 70} className="grid grid-cols-12 gap-4 py-8 border-b border-[#e6e2dc]">
                <div className="col-span-2 meta pt-1" style={{ color: accent }}>{p.n}</div>
                <div className="col-span-10">
                  <p className="font-display text-2xl md:text-3xl leading-tight">{p.title}</p>
                  <p className="mt-3 body">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-10 py-20 md:py-28">
        <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <p className="eyebrow md:col-span-5" style={{ color: accent }}>02 / Options</p>
          <h2 className="font-display h-section md:col-span-7">{s.tiersTitle[0]} <span style={{ color: accent }}>{s.tiersTitle[1]}</span></h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-4 border-t border-[#e6e2dc]">
          {s.tiers.map((t, i) => (
            <Reveal key={t.n} delay={i * 70} className={`p-6 md:p-8 border-b border-[#e6e2dc] ${i < 3 ? 'md:border-r' : ''} group`}>
              <div className="flex items-center justify-between">
                <p className="meta" style={{ color: accent }}>{t.n}</p>
                <ArrowUpRight size={16} className="text-[#c9c4bb] group-hover:text-[#141414] transition-colors" />
              </div>
              <p className="font-display text-2xl md:text-3xl mt-10 leading-tight">{t.title}</p>
              <p className="meta mt-6 normal-case tracking-normal text-[12px] text-[#6b6b6b]">{t.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-10 py-20 md:py-28 bg-warm">
        <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-8">
          <p className="eyebrow md:col-span-5" style={{ color: accent }}>03 / Questions</p>
          <h2 className="font-display h-section md:col-span-7">Know before <span style={{ color: accent }}>you book.</span></h2>
        </Reveal>
        <div className="max-w-4xl ml-auto">
          {s.faq.map((f) => (
            <details key={f.q} className="faq-item border-b border-[#e6e2dc] py-8 group" data-testid="service-faq-item">
              <summary className="flex items-start justify-between gap-6">
                <p className="font-display text-2xl md:text-3xl leading-tight">{f.q}</p>
                <span className="faq-plus mt-2"><Plus size={22} /></span>
              </summary>
              <p className="mt-6 pr-12 body max-w-2xl">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="body max-w-md">Start with a walkaround at the Ottawa studio, or build an estimate first.</p>
          <div className="flex items-center gap-4">
            <Link to="/build" data-testid="service-build-link" className="btn btn-red">Build your package <ArrowUpRight size={14} /></Link>
            <Link to="/#book" className="btn btn-outline">Book</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
